import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Alert, Container, Row, Col } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Field, ErrorMessage, FormikHelpers, Form as FormikForm } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types';
import { FaUser, FaPhone, FaLock, FaUserPlus, FaCheckCircle, FaGraduationCap, FaVideo, FaCertificate } from 'react-icons/fa';
import 'animate.css';
import './Auth.css';

interface RegisterFormValues {
  name: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
}

const Register: React.FC = () => {
  const { signUp, error } = useAuth();
  const [loading, setLoading] = useState(false);
  const [animate, setAnimate] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // تفعيل التحريكات بعد تحميل الصفحة
    setTimeout(() => {
      setAnimate(true);
    }, 100);
  }, []);

  const initialValues: RegisterFormValues = {
    name: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
  };

  const validationSchema = Yup.object({
    name: Yup.string()
      .required('الاسم مطلوب')
      .min(3, 'الاسم يجب أن يكون على الأقل 3 أحرف'),
    phoneNumber: Yup.string()
      .required('رقم الهاتف مطلوب')
      .matches(/^[0-9]+$/, 'رقم الهاتف يجب أن يحتوي على أرقام فقط')
      .min(10, 'رقم الهاتف غير صالح')
      .max(15, 'رقم الهاتف غير صالح'),
    password: Yup.string()
      .required('كلمة المرور مطلوبة')
      .min(6, 'كلمة المرور يجب أن تكون على الأقل 6 أحرف'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], 'كلمات المرور غير متطابقة')
      .required('تأكيد كلمة المرور مطلوب'),
  });

  const handleSubmit = async (
    values: RegisterFormValues,
    { setSubmitting }: FormikHelpers<RegisterFormValues>
  ) => {
    try {
      setLoading(true);
      // استخدام رقم الهاتف كبريد إلكتروني مؤقت للتسجيل
      const email = `${values.phoneNumber}@example.com`;
      await signUp(email, values.password, values.name, UserRole.STUDENT, values.phoneNumber);
      navigate('/student/dashboard');
    } catch (error) {
      console.error('Registration error:', error);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page register-page">
      <div className="auth-background"></div>
      <Container>
        <Row className="justify-content-center align-items-center min-vh-100 py-5">
          <Col md={10} lg={8} xl={7} className="px-3 px-md-4">
            <Card className={`auth-card border-0 shadow-lg ${animate ? 'animate__animated animate__fadeIn' : ''}`}>
              <Card.Header className="text-center py-4 auth-header">
                <div className="logo-container mb-3">
                  <FaGraduationCap size={40} className="logo-icon" />
                </div>
                <h2 className="mb-0 fw-bold">إنشاء حساب جديد</h2>
              </Card.Header>
              <Card.Body className="p-4 p-lg-5">
                <Row>
                  <Col lg={5} className="d-none d-lg-block pe-lg-4">
                    <div className={`text-center mb-5 ${animate ? 'animate__animated animate__fadeInRight animate__delay-1s' : ''}`}>
                      <div className="auth-icon-container mb-4">
                        <FaUserPlus size={40} className="auth-icon" />
                        <div className="auth-icon-ring"></div>
                      </div>
                      <h4 className="fw-bold mb-3">انضم إلينا اليوم</h4>
                      <p className="text-muted mb-4">أنشئ حسابك للوصول إلى جميع الدورات والمحتوى التعليمي</p>
                    </div>
                    
                    <div className={`auth-features ${animate ? 'animate__animated animate__fadeInUp animate__delay-1s' : ''}`}>
                      <div className="auth-feature mb-3">
                        <FaGraduationCap className="text-primary me-2" />
                        <span>دورات تعليمية متميزة</span>
                      </div>
                      <div className="auth-feature mb-3">
                        <FaVideo className="text-primary me-2" />
                        <span>محتوى مرئي عالي الجودة</span>
                      </div>
                      <div className="auth-feature mb-3">
                        <FaCertificate className="text-primary me-2" />
                        <span>شهادات معتمدة</span>
                      </div>
                    </div>
                  </Col>
                  
                  <Col lg={7} className="ps-lg-4">
                    <div className={`${animate ? 'animate__animated animate__fadeInLeft animate__delay-1s' : ''}`}>
                      {error && <Alert variant="danger" className="animate__animated animate__shakeX">{error}</Alert>}

                      <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                      >
                        {({ handleSubmit, isSubmitting, touched, errors }) => (
                          <FormikForm onSubmit={handleSubmit}>
                            <div className="mb-3">
                              <Form.Group controlId="name">
                                <Form.Label className="fw-bold">الاسم الكامل</Form.Label>
                                <div className={`input-group auth-input-group ${touched.name && errors.name ? 'is-invalid' : ''}`}>
                                  <span className="input-group-text">
                                    <FaUser className="text-primary" />
                                  </span>
                                  <Field
                                    as={Form.Control}
                                    type="text"
                                    name="name"
                                    placeholder="أدخل اسمك الكامل"
                                    className={`py-2 auth-input ${touched.name && errors.name ? 'is-invalid' : ''}`}
                                  />
                                </div>
                                <ErrorMessage
                                  name="name"
                                  component="div"
                                  className="text-danger mt-1 error-message"
                                />
                              </Form.Group>
                            </div>
                            
                            <div className="mb-3">
                              <Form.Group controlId="phoneNumber">
                                <Form.Label className="fw-bold">رقم الهاتف</Form.Label>
                                <div className={`input-group auth-input-group ${touched.phoneNumber && errors.phoneNumber ? 'is-invalid' : ''}`}>
                                  <span className="input-group-text">
                                    <FaPhone className="text-primary" />
                                  </span>
                                  <Field
                                    as={Form.Control}
                                    type="text"
                                    name="phoneNumber"
                                    placeholder="أدخل رقم هاتفك"
                                    className={`py-2 auth-input ${touched.phoneNumber && errors.phoneNumber ? 'is-invalid' : ''}`}
                                  />
                                </div>
                                <ErrorMessage
                                  name="phoneNumber"
                                  component="div"
                                  className="text-danger mt-1 error-message"
                                />
                              </Form.Group>
                            </div>

                            <div className="mb-3">
                              <Form.Group controlId="password">
                                <Form.Label className="fw-bold">كلمة المرور</Form.Label>
                                <div className={`input-group auth-input-group ${touched.password && errors.password ? 'is-invalid' : ''}`}>
                                  <span className="input-group-text">
                                    <FaLock className="text-primary" />
                                  </span>
                                  <Field
                                    as={Form.Control}
                                    type="password"
                                    name="password"
                                    placeholder="أدخل كلمة المرور"
                                    className={`py-2 auth-input ${touched.password && errors.password ? 'is-invalid' : ''}`}
                                  />
                                </div>
                                <ErrorMessage
                                  name="password"
                                  component="div"
                                  className="text-danger mt-1 error-message"
                                />
                              </Form.Group>
                            </div>
                            
                            <div className="mb-3">
                              <Form.Group controlId="confirmPassword">
                                <Form.Label className="fw-bold">تأكيد كلمة المرور</Form.Label>
                                <div className={`input-group auth-input-group ${touched.confirmPassword && errors.confirmPassword ? 'is-invalid' : ''}`}>
                                  <span className="input-group-text">
                                    <FaCheckCircle className="text-primary" />
                                  </span>
                                  <Field
                                    as={Form.Control}
                                    type="password"
                                    name="confirmPassword"
                                    placeholder="أعد إدخال كلمة المرور"
                                    className={`py-2 auth-input ${touched.confirmPassword && errors.confirmPassword ? 'is-invalid' : ''}`}
                                  />
                                </div>
                                <ErrorMessage
                                  name="confirmPassword"
                                  component="div"
                                  className="text-danger mt-1 error-message"
                                />
                              </Form.Group>
                            </div>

                            <Button
                              variant="primary"
                              type="submit"
                              className="w-100 py-2 mt-3 mb-3 d-flex align-items-center justify-content-center register-button"
                              disabled={loading || isSubmitting}
                            >
                              {loading ? (
                                <div className="spinner-container">
                                  <div className="spinner"></div>
                                  <span className="ms-2">جاري إنشاء الحساب...</span>
                                </div>
                              ) : (
                                <>
                                  <FaUserPlus className="me-2" />
                                  إنشاء حساب
                                </>
                              )}
                            </Button>
                            
                            <div className="text-center mt-4 pt-3 border-top">
                              <p className="mb-0">
                                لديك حساب بالفعل؟{' '}
                                <Link to="/login" className="text-primary fw-bold text-decoration-none login-link">تسجيل الدخول</Link>
                              </p>
                            </div>
                          </FormikForm>
                        )}
                      </Formik>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
              <Card.Footer className="bg-light text-center py-3">
                <p className="text-muted mb-0">  منصة السامي التعليمية. جميع الحقوق محفوظة</p>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Register;
